import type { ApiQuery, ApiRequestBody, ApiResponse } from "@takt/api-contracts"
import type { ApiClient } from "../client"

export type PublicCompaniesResponse = ApiResponse<"/v1/companies", "get", 200>
export type PublicCompany = PublicCompaniesResponse["results"][number]
export type CompanyDetails = ApiResponse<"/v1/companies/{companyId}", "get", 200>
export type FavoriteCompanyStatus = ApiResponse<"/v1/companies/{companyId}/favorite", "put", 200>
export type FavoriteCompaniesResponse = ApiResponse<"/v1/users/me/favorite-companies", "get", 200>
export type FavoriteCompany = FavoriteCompaniesResponse["results"][number]
export type CompanyServiceResponse = ApiResponse<"/v1/companies/{companyId}/services", "get", 200>
export type Service = CompanyServiceResponse["results"][number]
export type AvailabilityResult = ApiResponse<"/v1/companies/{companyId}/availability", "get", 200>
export type OwnAppointmentsResponse = ApiResponse<"/v1/users/me/appointments", "get", 200>
export type AppointmentRecord = OwnAppointmentsResponse["results"][number]
export type AppointmentStatus = AppointmentRecord["status"]
export type AppointmentBookedFrom = AppointmentRecord["bookedFrom"]
export type CompanyReviewsResponse = ApiResponse<"/v1/companies/{companyId}/reviews", "get", 200>
export type CompanyReview = CompanyReviewsResponse["results"][number]
export type CurrentUserReviewStatus = ApiResponse<"/v1/companies/{companyId}/reviews/me", "get", 200>
export type CurrentUserReview = ApiResponse<"/v1/companies/{companyId}/reviews/me", "put", 200>
export type ReviewReportReceipt = ApiResponse<"/v1/companies/{companyId}/reviews/{reviewId}/reports/me", "put", 200>
export type ReviewReportReason = ApiRequestBody<
    "/v1/companies/{companyId}/reviews/{reviewId}/reports/me",
    "put"
>["reason"]
export type AccountSummary = ApiResponse<"/v1/users/me/account-summary", "get", 200>
export type UserSettings = ApiResponse<"/v1/users/me/settings", "get", 200>
export type NotificationPreferences = ApiResponse<"/v1/notifications/preferences", "get", 200>
export type NotificationApplication = NotificationPreferences["application"]
export type PushSubscription = ApiResponse<"/v1/push/subscriptions/{installationId}", "get", 200>
export type PushApplication = PushSubscription["application"]
export type CompanyReviewModerationResponse = ApiResponse<"/v1/companies/{companyId}/reviews/moderation", "get", 200>
export type CompanyReviewModerationRecord = CompanyReviewModerationResponse["results"][number]

type CreateAppointmentBody = ApiRequestBody<"/v1/companies/{companyId}/appointments", "post">
type ListFavoriteCompaniesQuery = NonNullable<ApiQuery<"/v1/users/me/favorite-companies", "get">>
type ListCompanyReviewsQuery = NonNullable<ApiQuery<"/v1/companies/{companyId}/reviews", "get">>
type ListCompanyReviewsForModerationQuery = NonNullable<ApiQuery<"/v1/companies/{companyId}/reviews/moderation", "get">>
type CreateOrUpdateReviewBody = ApiRequestBody<"/v1/companies/{companyId}/reviews/me", "put">
type SubmitReviewReportBody = ApiRequestBody<"/v1/companies/{companyId}/reviews/{reviewId}/reports/me", "put">
type HideReviewBody = ApiRequestBody<"/v1/companies/{companyId}/reviews/{reviewId}/hide", "post">
type UpdateUserSettingsBody = ApiRequestBody<"/v1/users/me/settings", "patch">
type UpdateNotificationPreferencesBody = ApiRequestBody<"/v1/notifications/preferences", "patch">
type RegisterPushSubscriptionBody = ApiRequestBody<"/v1/push/subscriptions/{installationId}", "put">
type ListPublicCompaniesQuery = NonNullable<ApiQuery<"/v1/companies", "get">>

export type ListPublicCompaniesInput = {
    cursor?: string
    query?: string
    limit?: number
    lat?: number
    lng?: number
    radius?: number
    category?: string
    sortColumn?: ListPublicCompaniesQuery["sortColumn"]
    sortDirection?: ListPublicCompaniesQuery["sortDirection"]
}

export type ListOwnAppointmentsInput = {
    cursor?: string
    limit?: number
    range?: "upcoming" | "past"
    statuses?: AppointmentStatus[]
}

export type ListFavoriteCompaniesInput = {
    cursor?: string
    limit?: number
    sortColumn?: NonNullable<ListFavoriteCompaniesQuery["sortColumn"]>
    sortDirection?: NonNullable<ListFavoriteCompaniesQuery["sortDirection"]>
}

export type ListCompanyReviewsInput = {
    cursor?: string
    limit?: number
    sortColumn?: NonNullable<ListCompanyReviewsQuery["sortColumn"]>
    sortDirection?: NonNullable<ListCompanyReviewsQuery["sortDirection"]>
}

export type ListCompanyReviewsForModerationInput = ListCompanyReviewsInput & {
    visibility?: NonNullable<ListCompanyReviewsForModerationQuery["visibility"]>
}

export type CreateOrUpdateReviewInput = CreateOrUpdateReviewBody
export type SubmitReviewReportInput = SubmitReviewReportBody

export type HideReviewInput = HideReviewBody

export type UpdateUserSettingsInput = UpdateUserSettingsBody
export type UpdateNotificationPreferencesInput = UpdateNotificationPreferencesBody
export type RegisterPushSubscriptionInput = RegisterPushSubscriptionBody & { installationId: string }

export type AvailabilityInput =
    | {
          serviceIds: number[]
          dateFrom: string
          dateTo: string
          employeeId: string
          assignAnyEmployee?: never
      }
    | {
          serviceIds: number[]
          dateFrom: string
          dateTo: string
          employeeId?: never
          assignAnyEmployee: true
      }

type CreateAppointmentBase = Pick<CreateAppointmentBody, "serviceIds" | "startsAt"> & {
    idempotencyKey: string
    notesCustomer?: string | null
}

export type CreateCompanyAppointmentInput =
    | (CreateAppointmentBase & {
          employeeId: string
          assignAnyEmployee?: never
      })
    | (CreateAppointmentBase & {
          employeeId?: never
          assignAnyEmployee: true
      })

export type PublicCompanyBundle = ApiResponse<"/v1/companies/public/{slug}", "get", 200>

export function createCustomerEndpoints(client: ApiClient) {
    const listPublicCompanies = (input: ListPublicCompaniesInput = {}, signal?: AbortSignal) =>
        client.get<PublicCompaniesResponse>("/companies", { query: input, signal })

    const getPublicCompanyBySlug = (slug: string, signal?: AbortSignal) =>
        client.get<ApiResponse<"/v1/companies/public/{slug}", "get", 200>>(
            `/companies/public/${encodeURIComponent(slug)}`,
            { signal },
        )

    const getCompanyDetails = (companyId: number, signal?: AbortSignal) =>
        client.get<CompanyDetails>(`/companies/${companyId}`, { signal })

    const listCompanyServices = (companyId: number, signal?: AbortSignal) =>
        client.get<CompanyServiceResponse>(`/companies/${companyId}/services`, {
            query: { limit: 50 },
            signal,
        })

    return {
        listPublicCompanies,
        getPublicCompanyBySlug,
        getCompanyDetails,
        listCompanyServices,
        async getPublicCompanyBundleBySlug(slug: string, signal?: AbortSignal): Promise<PublicCompanyBundle> {
            return await getPublicCompanyBySlug(slug, signal)
        },
        getCompanyAvailability(companyId: number, input: AvailabilityInput, signal?: AbortSignal) {
            return client.get<AvailabilityResult>(`/companies/${companyId}/availability`, {
                query: input,
                signal,
            })
        },
        listFavoriteCompanies(input: ListFavoriteCompaniesInput = {}, signal?: AbortSignal) {
            return client.get<FavoriteCompaniesResponse>("/users/me/favorite-companies", {
                query: input,
                signal,
            })
        },
        favoriteCompany(companyId: number) {
            return client.put<FavoriteCompanyStatus>(`/companies/${companyId}/favorite`)
        },
        unfavoriteCompany(companyId: number) {
            return client.delete<null>(`/companies/${companyId}/favorite`)
        },
        listOwnAppointments(input: ListOwnAppointmentsInput = {}, signal?: AbortSignal) {
            return client.get<OwnAppointmentsResponse>("/users/me/appointments", {
                query: input,
                signal,
            })
        },
        getAccountSummary(signal?: AbortSignal) {
            return client.get<AccountSummary>("/users/me/account-summary", { signal })
        },
        updateCurrentUserSettings(input: UpdateUserSettingsInput) {
            return client.patch<UserSettings>("/users/me/settings", input)
        },
        getNotificationPreferences(application: NotificationApplication, signal?: AbortSignal) {
            return client.get<NotificationPreferences>("/notifications/preferences", {
                query: { application },
                signal,
            })
        },
        updateNotificationPreferences(input: UpdateNotificationPreferencesInput) {
            return client.patch<NotificationPreferences>("/notifications/preferences", input)
        },
        registerPushSubscription(input: RegisterPushSubscriptionInput) {
            const { installationId, ...body } = input
            return client.put<PushSubscription>(`/push/subscriptions/${encodeURIComponent(installationId)}`, body)
        },
        getPushSubscription(installationId: string, application: PushApplication, signal?: AbortSignal) {
            return client.get<PushSubscription>(`/push/subscriptions/${encodeURIComponent(installationId)}`, {
                query: { application },
                signal,
            })
        },
        revokePushSubscription(installationId: string, application: PushApplication) {
            return client.delete<null>(`/push/subscriptions/${encodeURIComponent(installationId)}`, {
                query: { application },
            })
        },
        createCompanyAppointment(companyId: number, input: CreateCompanyAppointmentInput) {
            const { idempotencyKey, ...body } = input
            return client.post<ApiResponse<"/v1/companies/{companyId}/appointments", "post", 201>>(
                `/companies/${companyId}/appointments`,
                body,
                { headers: { "Idempotency-Key": idempotencyKey } },
            )
        },
        cancelOwnAppointment(appointmentId: number) {
            return client.post<ApiResponse<"/v1/users/me/appointments/{appointmentId}/cancel", "post", 200>>(
                `/users/me/appointments/${appointmentId}/cancel`,
            )
        },
        listCompanyReviews(companyId: number, input: ListCompanyReviewsInput = {}, signal?: AbortSignal) {
            return client.get<CompanyReviewsResponse>(`/companies/${companyId}/reviews`, {
                query: input,
                signal,
            })
        },
        getCurrentUserReviewStatus(companyId: number, signal?: AbortSignal) {
            return client.get<CurrentUserReviewStatus>(`/companies/${companyId}/reviews/me`, { signal })
        },
        upsertCurrentUserReview(companyId: number, input: CreateOrUpdateReviewInput) {
            return client.put<CurrentUserReview>(`/companies/${companyId}/reviews/me`, input)
        },
        submitReviewReport(companyId: number, reviewId: number, input: SubmitReviewReportInput) {
            return client.put<ReviewReportReceipt>(`/companies/${companyId}/reviews/${reviewId}/reports/me`, input)
        },
        listCompanyReviewsForModeration(
            companyId: number,
            input: ListCompanyReviewsForModerationInput = {},
            signal?: AbortSignal,
        ) {
            return client.get<CompanyReviewModerationResponse>(`/companies/${companyId}/reviews/moderation`, {
                query: input,
                signal,
            })
        },
        hideCompanyReview(companyId: number, reviewId: number, input: HideReviewInput = {}) {
            return client.post<CompanyReviewModerationRecord>(`/companies/${companyId}/reviews/${reviewId}/hide`, input)
        },
        restoreCompanyReview(companyId: number, reviewId: number) {
            return client.post<CompanyReviewModerationRecord>(`/companies/${companyId}/reviews/${reviewId}/restore`)
        },
    }
}
